import Papa from 'papaparse';

const OFFER_ID_HEADERS = ['offer_id', 'offerid', 'offer id', 'property_id', 'propertyid', 'property id'];

export type ParseOfferIdsCsvSuccess = { ok: true; offerIds: Array<string> };
export type ParseOfferIdsCsvFailure = { ok: false; error: string };
export type ParseOfferIdsCsvResult = ParseOfferIdsCsvSuccess | ParseOfferIdsCsvFailure;

function normaliseHeader(header: string): string {
  return header.trim().toLowerCase().replace(/\s+/g, '_');
}

export function parseOfferIdsCsv(fileContent: string): ParseOfferIdsCsvResult {
  const trimmedContent = fileContent.trim();

  if (!trimmedContent) {
    return { ok: false as const, error: 'The CSV file is empty.' };
  }

  const parsed = Papa.parse<Record<string, string>>(trimmedContent, {
    header: true,
    delimiter: ',',
    skipEmptyLines: 'greedy',
    transformHeader: normaliseHeader,
  });

  const fatalErrors = parsed.errors.filter(
    (error) => error.code !== 'UndetectableDelimiter' && error.code !== 'TooFewFields',
  );
  if (fatalErrors.length > 0) {
    return { ok: false as const, error: 'Could not parse the CSV file. Please check the format and try again.' };
  }

  const fields = (parsed.meta.fields ?? []).map(normaliseHeader);
  const offerIdHeader = fields.find((field) => OFFER_ID_HEADERS.includes(field));

  if (!offerIdHeader) {
    return { ok: false as const, error: 'CSV must include an offer_id or property_id column.' };
  }

  const offerIds = Array.from(new Set(parsed.data.map((row) => (row[offerIdHeader] ?? '').trim()).filter(Boolean)));

  if (!offerIds.length) {
    return { ok: false as const, error: 'No offer IDs were found in the CSV file.' };
  }

  return { ok: true as const, offerIds };
}

export function isParseOfferIdsCsvFailure(result: ParseOfferIdsCsvResult): result is ParseOfferIdsCsvFailure {
  return result.ok === false;
}

export function readCsvFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      reject(new Error('Please upload a CSV file.'));
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Could not read the CSV file.'));
        return;
      }
      resolve(reader.result);
    };

    reader.onerror = () => {
      reject(new Error('Could not read the CSV file.'));
    };

    reader.readAsText(file);
  });
}
